/**
 * useTouchDragDrop - Polyfill for HTML5 drag-and-drop on touch devices (iPad/tablet)
 * 
 * HTML5 draggable doesn't work on touch devices. This hook intercepts touch events
 * and simulates drag/drop by tracking the dragged element and drop target.
 * 
 * Usage:
 *   const { touchHandlers, dragState } = useTouchDragDrop({ onReorder });
 *   <div {...touchHandlers(index)} data-drag-index={index}>...</div>
 */
import { useRef, useCallback, useState } from 'react';

export function useTouchDragDrop({ onReorder, axis = 'vertical' }) {
  const dragRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const handleTouchStart = useCallback((index, e) => {
    // Only activate on long press or specific grip handle
    const target = e.target;
    const isGrip = target.closest('[data-drag-grip]');
    if (!isGrip) return; // Only drag from grip handle
    
    e.preventDefault();
    const touch = e.touches[0];
    dragRef.current = {
      index,
      startY: touch.clientY,
      startX: touch.clientX,
      element: target.closest('[data-drag-index]'),
    };
    setDragIdx(index);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    
    // Find element under touch point
    const elemBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!elemBelow) return;
    
    const dropTarget = elemBelow.closest('[data-drag-index]');
    if (dropTarget) {
      const newIdx = parseInt(dropTarget.getAttribute('data-drag-index'), 10);
      if (!isNaN(newIdx) && newIdx !== overIdx) {
        setOverIdx(newIdx);
      }
    }
  }, [overIdx]);

  const handleTouchEnd = useCallback(() => {
    if (dragRef.current && overIdx !== null && dragRef.current.index !== overIdx) {
      onReorder(dragRef.current.index, overIdx);
    }
    dragRef.current = null;
    setDragIdx(null);
    setOverIdx(null);
  }, [overIdx, onReorder]);

  const touchHandlers = useCallback((index) => ({
    'data-drag-index': index,
    onTouchStart: (e) => handleTouchStart(index, e),
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  }), [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { touchHandlers, dragIdx, overIdx };
}
